import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AutoAwesome as AiIcon,
  CheckCircle as ApproveIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  HelpOutline as AskAiIcon,
} from '@mui/icons-material';
import type { MappingResponse, HealthIssue } from '../../services/api';
import type { CanvasSelection } from '../types';
import type { ReviewItem } from '../hooks/useReviewItems';
import type { MappingTableRowData, MappingTableSort } from './types';
import { hasAnyLevel } from '../../lib/outputStructure';
import { useMappingTableState } from './useMappingTableState';
import { useFieldValidation } from '../hooks/useFieldValidation';
import MappingGroupHeader from './MappingGroupHeader';
import MappingTableRow from './MappingTableRow';
import MappingTableToolbar from './MappingTableToolbar';

export interface MappingTableViewProps {
  mappingResponse?: MappingResponse | null;
  onSelectionChange?: (selection: CanvasSelection | null) => void;
  onApproveSelected?: (ids: string[]) => void;
  onDeleteSelected?: (ids: string[]) => void;
  onAiRegenSelected?: (ids: string[]) => void;
  /** Inline edit save callback — receives mapping id + changed values */
  onInlineEditSave?: (row: MappingTableRowData, values: import('./MappingTableRow').InlineEditValues) => void;
  /** External search query from sidebar — synced to internal filter */
  externalSearchQuery?: string;
  /** Filter rows to only those involving selected source/target tables */
  selectedTargetIds?: Set<string>;
  selectedSourceIds?: Set<string>;
  /** Health issues from the validation layer, for per-row indicators */
  healthIssues?: HealthIssue[];
  /** Trigger data preview for a specific target table */
  onPreview?: (targetTable: string) => void;
  /** When present, shows a Hierarchy Path column with each mapping's position in the output structure. */
  outputStructure?: Record<string, any> | null;
  /**
   * Issue #656: when set, scroll the matching `[data-row-id]` row into
   * view. Format: `"target_table.target_field"` (lowercase). Driven by
   * the unified Review pane → canvas selection pipeline.
   */
  fieldSelectionRowId?: string | null;
  /**
   * Issue #656: when present, per-row ⚠ chips are sourced from the
   * unified `useReviewItems` stream (keyed on lowercase
   * `${target_table}.${target_field}`) so counts match the Review pane
   * exactly. Falls back to `healthIssues` when not provided.
   */
  reviewItemsByField?: Map<string, ReviewItem[]>;
}

/** Column definitions for the table header. */
const BASE_COLUMNS: Array<{ id: MappingTableSort['field'] | 'checkbox' | 'approval' | 'actions' | 'hierarchyPath'; label: string; sortable: boolean; width?: number | string; align?: 'left' | 'center' | 'right' }> = [
  { id: 'checkbox', label: '', sortable: false, width: 36 },
  { id: 'status', label: '', sortable: true, width: 28 },
  { id: 'source', label: 'Source', sortable: true, width: 200 },
  { id: 'transformation', label: 'Transformation', sortable: true },
  { id: 'target', label: 'Target', sortable: true, width: 180 },
  { id: 'confidence', label: 'Conf.', sortable: true, width: 70, align: 'center' },
  { id: 'approval', label: 'Status', sortable: false, width: 90 },
  { id: 'actions', label: '', sortable: false, width: 48 },
];

const HIERARCHY_COLUMN: typeof BASE_COLUMNS[number] = { id: 'hierarchyPath', label: 'Hierarchy Path', sortable: false, width: 180 };

const MappingTableView: React.FC<MappingTableViewProps> = ({
  mappingResponse,
  onSelectionChange,
  onApproveSelected,
  onDeleteSelected,
  onAiRegenSelected,
  onInlineEditSave,
  externalSearchQuery,
  selectedTargetIds,
  selectedSourceIds,
  healthIssues,
  onPreview,
  outputStructure,
  fieldSelectionRowId,
  reviewItemsByField,
}) => {
  // Issue #656: scroll selected row into view when an external
  // selection (review pane or canvas) addresses it.
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (!fieldSelectionRowId || !containerRef.current) return;
    const el = containerRef.current.querySelector<HTMLElement>(
      `[data-row-id="${CSS.escape(fieldSelectionRowId)}"]`,
    );
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [fieldSelectionRowId]);
  const showHierarchyColumn = hasAnyLevel(outputStructure as any);
  const COLUMNS = showHierarchyColumn
    ? [...BASE_COLUMNS.slice(0, 5), HIERARCHY_COLUMN, ...BASE_COLUMNS.slice(5)]
    : BASE_COLUMNS;
  const COL_SPAN = COLUMNS.length;
  const {
    sortedRows,
    groupedRows,
    sort,
    filter,
    setFilter,
    selectedIds,
    handleToggleSelect,
    handleSelectAll,
    handleDeselectAll,
    handleToggleSort,
  } = useMappingTableState({ mappingResponse });

  // Active row: the row whose inspector panel is open (clicked to inspect)
  const [activeRowId, setActiveRowId] = useState<string | null>(null);

  // Inline edit state: which row id has its editor open
  const [inlineEditRowId, setInlineEditRowId] = useState<string | null>(null);
  const handleToggleInlineEdit = useCallback((rowId: string) => {
    setInlineEditRowId((prev) => (prev === rowId ? null : rowId));
  }, []);

  // Extract source options from mapping response for inline edit dropdowns
  const { sourceFiles, sourceFieldsByFile } = useMemo(() => {
    if (!mappingResponse) return { sourceFiles: [] as string[], sourceFieldsByFile: {} as Record<string, string[]> };
    const fieldsByFile: Record<string, string[]> = {};
    const files: string[] = [];
    const seen = new Set<string>();
    const metadata = mappingResponse.metadata;

    // Try source_schema_set_snapshot first
    const snapshot = metadata?.source_schema_set_snapshot;
    if (snapshot?.tables) {
      Object.entries(snapshot.tables).forEach(([tableName, tableDef]) => {
        if (!seen.has(tableName)) { seen.add(tableName); files.push(tableName); }
        const cols = tableDef?.columns;
        if (Array.isArray(cols)) {
          fieldsByFile[tableName] = cols.map((c) => c.name).filter(Boolean);
        } else if (cols && typeof cols === 'object') {
          fieldsByFile[tableName] = Object.keys(cols);
        }
      });
    }

    // Fallback to mappings
    if (files.length === 0 && Array.isArray(mappingResponse.mappings)) {
      mappingResponse.mappings.forEach((m) => {
        const sf = m.source_file?.trim();
        if (sf && !seen.has(sf)) { seen.add(sf); files.push(sf); }
      });
      files.forEach((f) => {
        const fields = new Set<string>();
        mappingResponse.mappings?.forEach((m) => {
          if (m.source_file?.trim() === f && m.source_field?.trim()) fields.add(m.source_field.trim());
        });
        fieldsByFile[f] = Array.from(fields);
      });
    }

    return { sourceFiles: files, sourceFieldsByFile: fieldsByFile };
  }, [mappingResponse]);

  // Sync external search query from sidebar into the internal filter
  useEffect(() => {
    if (externalSearchQuery !== undefined) {
      setFilter((prev) => {
        const next = externalSearchQuery || undefined;
        if (prev.searchQuery === next) return prev;
        return { ...prev, searchQuery: next };
      });
    }
  }, [externalSearchQuery, setFilter]);

  // Apply sidebar table selection filter
  const hasTableFilter = (selectedTargetIds?.size ?? 0) + (selectedSourceIds?.size ?? 0) > 0;
  const tableFilteredRows = useMemo(() => {
    if (!hasTableFilter) return sortedRows;
    return sortedRows.filter((row) => {
      const tgtMatch = !selectedTargetIds?.size || selectedTargetIds.has(row.targetTable);
      const srcMatch = !selectedSourceIds?.size || selectedSourceIds.has(row.sourceTable);
      return tgtMatch && srcMatch;
    });
  }, [sortedRows, hasTableFilter, selectedTargetIds, selectedSourceIds]);

  const tableFilteredGroupedRows = useMemo(() => {
    if (!hasTableFilter) return groupedRows;
    const groups = new Map<string, MappingTableRowData[]>();
    tableFilteredRows.forEach((row) => {
      const key = row.targetTable || '(no table)';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(row);
    });
    return groups;
  }, [tableFilteredRows, hasTableFilter, groupedRows]);

  // Row context menu state
  const [rowMenu, setRowMenu] = useState<{
    anchorEl: Element;
    row: MappingTableRowData;
  } | null>(null);

  const handleRowClick = useCallback(
    (row: MappingTableRowData) => {
      setActiveRowId(row.id);
      if (!onSelectionChange) return;
      onSelectionChange({
        kind: 'node',
        node: {
          id: `targetField:${row.targetTable}.${row.targetField}`,
          type: 'targetField',
          position: { x: 0, y: 0 },
          data: {
            kind: 'targetField',
            label: row.targetField,
            qualifiedName: `${row.targetTable}.${row.targetField}`,
            table: row.targetTable,
            field: row.targetField,
            mappingIds: row.mapping ? [row.id] : [],
            mapping: row.mapping,
            confidence: row.confidence,
            aiGenerated: row.aiGenerated,
            isUnmapped: !row.isMapped,
          },
        },
      });
    },
    [onSelectionChange],
  );

  const handleOpenRowMenu = useCallback(
    (event: React.MouseEvent, row: MappingTableRowData) => {
      setRowMenu({ anchorEl: event.currentTarget, row });
    },
    []
  );

  const handleCloseRowMenu = useCallback(() => {
    setRowMenu(null);
  }, []);

  const handleApproveSelected = useMemo(
    () => onApproveSelected ? () => onApproveSelected(Array.from(selectedIds)) : undefined,
    [onApproveSelected, selectedIds]
  );

  const hasPendingInSelection = useMemo(() => {
    if (selectedIds.size === 0) return false;
    return Array.from(selectedIds).some((id) => {
      const row = sortedRows.find((r) => r.id === id);
      return row && row.approvalStatus !== 'approved';
    });
  }, [selectedIds, sortedRows]);
  const handleDeleteSelected = useMemo(
    () => onDeleteSelected ? () => onDeleteSelected(Array.from(selectedIds)) : undefined,
    [onDeleteSelected, selectedIds]
  );
  const handleAiRegenSelected = useMemo(
    () => onAiRegenSelected ? () => onAiRegenSelected(Array.from(selectedIds)) : undefined,
    [onAiRegenSelected, selectedIds]
  );

  // Real-time type + join validation via client-side metadata
  const { validateField, validateJoinPath } = useFieldValidation(mappingResponse);

  // Callback for inline editor: when source field changes, compute type issues
  const handleSourceChanged = useCallback(
    (row: MappingTableRowData) => (sourceFile: string, sourceField: string) => {
      return validateField({
        sourceFile,
        sourceField,
        targetTable: row.targetTable,
        targetField: row.targetField,
        transformation: undefined,
        transformationType: 'direct',
      });
    },
    [validateField]
  );

  // Pre-compute join warnings for each row
  const joinWarningsByRow = useMemo(() => {
    const map = new Map<string, import('../validation/joinPathValidator').JoinPathWarning | null>();
    for (const row of sortedRows) {
      if (!row.isMapped || !row.sourceTable) continue;
      const warning = validateJoinPath(row.sourceTable, row.targetTable);
      if (warning) map.set(row.id, warning);
    }
    return map;
  }, [sortedRows, validateJoinPath]);

  const totalUnmapped = useMemo(() => sortedRows.filter(r => !r.isMapped).length, [sortedRows]);

  // Build health-issue lookup: key = "table.field" → issues.
  // Issue #656: prefer `reviewItemsByField` (unified `useReviewItems`
  // stream) so per-row chip counts match the Review pane exactly. Each
  // ReviewItem is projected to a HealthIssue-shaped record so the
  // existing chip render is unchanged.
  const healthIssuesByField = useMemo(() => {
    const map = new Map<string, HealthIssue[]>();
    if (reviewItemsByField && reviewItemsByField.size > 0) {
      reviewItemsByField.forEach((items, _key) => {
        for (const it of items) {
          if (!it.target_field) continue;
          const lcKey = `${it.target_table.toLowerCase()}.${it.target_field.toLowerCase()}`;
          const projected: HealthIssue = {
            category:
              (it.payload as HealthIssue)?.category ??
              (it.bucket === 'unmapped' ? 'coverage' : it.bucket),
            severity: it.severity,
            target_table: it.target_table,
            target_field: it.target_field,
            message: it.message,
            rule_type: (it.payload as HealthIssue)?.rule_type,
            sample_violations: (it.payload as HealthIssue)?.sample_violations,
            row_indices: (it.payload as HealthIssue)?.row_indices,
            count: (it.payload as HealthIssue)?.count,
            remediation: (it.payload as HealthIssue)?.remediation,
            fix_actions: (it.payload as HealthIssue)?.fix_actions,
          };
          if (!map.has(lcKey)) map.set(lcKey, []);
          map.get(lcKey)!.push(projected);
        }
      });
      return map;
    }
    if (!healthIssues) return map;
    healthIssues.forEach((issue) => {
      if (!issue.target_field) return;
      const key = `${(issue.target_table || '').toLowerCase()}.${issue.target_field.toLowerCase()}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(issue);
    });
    return map;
  }, [healthIssues, reviewItemsByField]);

  // Issue #656: in-table validation/unmapped banners removed — the
  // unified Review pane is now the single review surface. Per-row
  // health chips on `MappingTableRow` still surface field-level
  // warnings inline.

  // Detect operation groups within each target table group
  const OP_TYPES = useMemo(() => new Set(['aggregation', 'window', 'pivot', 'unpivot']), []);
  const opTypeLabel = useMemo<Record<string, { badge: string; label: string; color: string }>>(() => ({
    aggregation: { badge: 'AGG', label: 'AGGREGATE', color: '#7c3aed' },
    window: { badge: 'WIN', label: 'WINDOW', color: '#db2777' },
    pivot: { badge: 'PVT', label: 'PIVOT', color: '#0891b2' },
    unpivot: { badge: 'UNP', label: 'UNPIVOT', color: '#0891b2' },
  }), []);

  // Pre-compute operation group headers per table group to avoid rebuilding on every render.
  // Maps targetTable → array of { row, opHeaderKey?, opHeaderCfg?, groupByInfo? }
  type OpGroupItem = { row: MappingTableRowData; opHeaderKey?: string; opHeaderCfg?: { badge: string; label: string; color: string }; groupByInfo?: string };
  const opGroupMeta = useMemo(() => {
    const meta = new Map<string, OpGroupItem[]>();
    Array.from(tableFilteredGroupedRows.entries()).forEach(([targetTable, rows]) => {
      const items: OpGroupItem[] = [];
      let currentOpType: string | null = null;
      rows.forEach((row: MappingTableRowData) => {
        const rowOpType = OP_TYPES.has(row.transformationType) ? row.transformationType : null;
        if (rowOpType && rowOpType !== currentOpType) {
          const cfg = opTypeLabel[rowOpType] ?? { badge: rowOpType.toUpperCase(), label: rowOpType.toUpperCase(), color: '#7c3aed' };
          const groupByInfo = row.mapping?.transformation_steps
            ?.find((s: { operation?: string }) => s.operation === 'group_by' || s.operation === 'partition_by')
            ?.expression;
          items.push({ row, opHeaderKey: `op-header-${targetTable}-${rowOpType}`, opHeaderCfg: cfg, groupByInfo });
        } else {
          items.push({ row });
        }
        currentOpType = rowOpType;
      });
      meta.set(targetTable, items);
    });
    return meta;
  }, [tableFilteredGroupedRows, OP_TYPES, opTypeLabel]);

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Bulk operations toolbar */}
      <MappingTableToolbar
        selectedCount={selectedIds.size}
        onDeselectAll={handleDeselectAll}
        onApproveSelected={hasPendingInSelection ? handleApproveSelected : undefined}
        onDeleteSelected={handleDeleteSelected}
        onAiRegenSelected={handleAiRegenSelected}
      />

      {/* Table */}
      <TableContainer ref={containerRef as any} sx={{ flex: 1, overflow: 'auto' }}>
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: 'fixed',
            width: '100%',
          }}
        >
          <TableHead>
            <TableRow>
              {COLUMNS.map((col) => (
                <TableCell
                  key={col.id}
                  align={col.align ?? 'left'}
                  sx={{
                    width: col.width,
                    fontWeight: 700,
                    fontSize: 12,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    py: 1,
                    backgroundColor: '#f8fafc',
                    borderBottom: '2px solid',
                    borderColor: 'divider',
                  }}
                >
                  {col.sortable ? (
                    <TableSortLabel
                      active={sort.field === col.id}
                      direction={sort.field === col.id ? sort.direction : 'asc'}
                      onClick={() =>
                        handleToggleSort(col.id as MappingTableSort['field'])
                      }
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from(opGroupMeta.entries()).map(([targetTable, items]) => (
              <React.Fragment key={targetTable}>
                <MappingGroupHeader targetTable={targetTable} rows={tableFilteredGroupedRows.get(targetTable)!} onPreview={onPreview} />
                {items.map((item) => (
                  <React.Fragment key={item.row.id}>
                    {item.opHeaderCfg && (
                      <TableRow key={item.opHeaderKey} sx={{ backgroundColor: alpha(item.opHeaderCfg.color, 0.06) }}>
                        <TableCell colSpan={COL_SPAN} sx={{ py: 0.75, px: 2, borderBottom: `1px solid ${alpha(item.opHeaderCfg.color, 0.15)}` }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              size="small"
                              label={item.opHeaderCfg.badge}
                              sx={{
                                height: 20,
                                fontSize: 10,
                                fontWeight: 800,
                                backgroundColor: alpha(item.opHeaderCfg.color, 0.15),
                                color: item.opHeaderCfg.color,
                                letterSpacing: '0.05em',
                              }}
                            />
                            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 12, color: item.opHeaderCfg.color, letterSpacing: '0.02em' }}>
                              {item.opHeaderCfg.label}{item.groupByInfo ? `: ${item.groupByInfo}` : ''}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                    <MappingTableRow
                      row={item.row}
                      selected={selectedIds.has(item.row.id)}
                      active={activeRowId === item.row.id}
                      onToggleSelect={handleToggleSelect}
                      onRowClick={handleRowClick}
                      onOpenContextMenu={handleOpenRowMenu}
                      inlineEditOpen={inlineEditRowId === item.row.id}
                      onToggleInlineEdit={handleToggleInlineEdit}
                      onInlineEditSave={onInlineEditSave}
                      sourceFiles={sourceFiles}
                      sourceFieldsByFile={sourceFieldsByFile}
                      colSpan={COL_SPAN}
                      healthIssues={healthIssuesByField.get(`${item.row.targetTable.toLowerCase()}.${item.row.targetField.toLowerCase()}`)}
                      onSourceChanged={inlineEditRowId === item.row.id ? handleSourceChanged(item.row) : undefined}
                      joinWarning={joinWarningsByRow.get(item.row.id) ?? null}
                      showHierarchyPath={showHierarchyColumn}
                    />
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
            {tableFilteredRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={COL_SPAN} sx={{ textAlign: 'center', py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    {filter.type === 'all' && !filter.searchQuery
                      ? 'No mappings available. Generate mappings first.'
                      : 'No results match the current filter.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Per-row context menu */}
      <Menu
        open={Boolean(rowMenu)}
        onClose={handleCloseRowMenu}
        anchorEl={rowMenu?.anchorEl ?? null}
        PaperProps={{
          sx: {
            minWidth: 180,
            boxShadow: '0 8px 24px rgba(15,23,42,0.14)',
            borderRadius: 2,
          },
        }}
      >
        {rowMenu?.row.isMapped ? [
          <MenuItem
            key="edit"
            onClick={() => { handleRowClick(rowMenu.row); handleCloseRowMenu(); }}
          >
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Edit in Inspector" primaryTypographyProps={{ fontSize: 13 }} />
          </MenuItem>,
          <MenuItem
            key="ai-regen"
            onClick={() => { onAiRegenSelected?.([rowMenu.row.id]); handleCloseRowMenu(); }}
          >
            <ListItemIcon><AiIcon fontSize="small" sx={{ color: '#7c3aed' }} /></ListItemIcon>
            <ListItemText primary="AI Regenerate" primaryTypographyProps={{ fontSize: 13 }} />
          </MenuItem>,
          <MenuItem
            key="ask-ai"
            onClick={() => { handleRowClick(rowMenu.row); handleCloseRowMenu(); }}
          >
            <ListItemIcon><AskAiIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Ask AI About This" primaryTypographyProps={{ fontSize: 13 }} />
          </MenuItem>,
          ...(rowMenu.row.approvalStatus !== 'approved'
            ? [
                <MenuItem
                  key="approve"
                  onClick={() => { onApproveSelected?.([rowMenu.row.id]); handleCloseRowMenu(); }}
                >
                  <ListItemIcon><ApproveIcon fontSize="small" sx={{ color: '#10b981' }} /></ListItemIcon>
                  <ListItemText primary="Approve" primaryTypographyProps={{ fontSize: 13 }} />
                </MenuItem>,
              ]
            : []),
          <Divider key="div" sx={{ my: 0.5 }} />,
          <MenuItem
            key="delete"
            onClick={() => { onDeleteSelected?.([rowMenu.row.id]); handleCloseRowMenu(); }}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText primary="Delete Mapping" primaryTypographyProps={{ fontSize: 13 }} />
          </MenuItem>,
        ] : [
          <MenuItem
            key="map"
            onClick={(e) => {
              e.stopPropagation();
              const row = rowMenu!.row;
              handleCloseRowMenu();
              requestAnimationFrame(() => handleRowClick(row));
            }}
          >
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Map Field" primaryTypographyProps={{ fontSize: 13 }} />
          </MenuItem>,
          <MenuItem
            key="ai-suggest"
            onClick={() => { handleCloseRowMenu(); }}
          >
            <ListItemIcon><AiIcon fontSize="small" sx={{ color: '#7c3aed' }} /></ListItemIcon>
            <ListItemText primary="AI Suggest" primaryTypographyProps={{ fontSize: 13 }} />
          </MenuItem>,
        ]}
      </Menu>
    </Box>
  );
};

export default React.memo(MappingTableView);
